/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2021, Zumper
 * @description  buildSrcSet.ts
 */

import { ImageCdnParams } from './ImageCdnParams'
import { toImageCdnUrl } from './toImageCdnUrl'

/** Example: [1, 2, 3] */
export type PixelRatios = number[]

/**
 * Allows for setting the quality param in tandem with the pixel ratio param
 * Example: { 1: 80, 2: 75, 3: 50 }
 */
export interface PixelRatiosWithQuality {
  [pixelRatio: number]: number
}

export const defaultPixelRatios: PixelRatios = [1, 2, 3, 4]

export const buildSrcSet = (
  src: string,
  params?: ImageCdnParams,
  options: PixelRatios | PixelRatiosWithQuality = defaultPixelRatios
) => {
  if (!src) {
    return undefined
  }
  // NOTE: we re-use this url and overwrite searchParams for each pixel ratio
  const url = toImageCdnUrl(src, params)
  if (!url) {
    return undefined
  }
  const existingPixelRatio = url.searchParams.get('dpr')
  const existingQuality = url.searchParams.get('q')

  let srcSet = ''

  // allow for setting quality with each pixel ratio
  const pixelRatios = Array.isArray(options)
    ? options
    : (Object.entries(options) as any as Array<[number, number]>)

  for (const entry of pixelRatios) {
    const pixelRatio = Array.isArray(entry) ? entry[0] : entry
    const quality = Array.isArray(entry) ? entry[1] : undefined

    // overwrite dpr and q
    url.searchParams.set('dpr', `${pixelRatio}`)
    if (quality !== undefined) {
      url.searchParams.set('q', quality.toString())
    }

    srcSet += `${srcSet ? ', ' : ''}${url.toString()} ${pixelRatio}x`

    // cleanup (avoid leaking values to the next pixelRatio)
    if (existingPixelRatio !== null) {
      url.searchParams.set('dpr', existingPixelRatio)
    } else {
      url.searchParams.delete('dpr')
    }
    if (quality !== undefined && existingQuality !== null) {
      url.searchParams.set('q', existingQuality)
    } else if (quality !== undefined) {
      url.searchParams.delete('q')
    }
  }
  return srcSet
}
